/**
 * Root widget Preact component.
 *
 * Owns:
 *  - Open/close state of the panel.
 *  - Conversation history (in memory; reset on page reload, by
 *    design, since this is the customer's first-touch chat).
 *  - Streaming + error state.
 *  - First-load fetch of `/widget/v1/config` for branding + opener.
 *
 * Layout intent (May 2026 redesign):
 *  - Compact panel, voice-first composer when `tenant.voice_enabled`.
 *  - The composer's right slot holds exactly one button: the mic when
 *    the input is empty, the send button when the operator typed
 *    something. This keeps the affordance unambiguous.
 *  - The empty state uses Yara's avatar + opener line + two
 *    suggested-prompt chips so the panel feels warm before the first
 *    message lands.
 *
 * Authoring rule: keep this file under 300 lines and free of API or
 * SSE-parsing details. Those live in `api.ts`. CLAUDE.md sectie 7.
 */

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  PollError,
  type PolledMessage,
  UploadError,
  type UploadErrorKind,
  buildRequestMessages,
  fetchConfig,
  fetchNewMessages,
  streamChat,
  submitCustomerEmail,
  uploadAttachment,
} from "./api";
import { format, resolveLocale, tFor } from "./i18n";
import { AttachIcon, BackArrowIcon, ChatIcon, CloseIcon, ResetIcon, SendIcon } from "./icons";
import type {
  ChatTurn,
  EmailCaptureState,
  PendingAttachment,
  Status,
  WidgetBootConfig,
  WidgetTenantConfig,
} from "./types";
import { clearVisitorSession, loadVisitorSession, saveVisitorSession } from "./visitor_session";
import { VoiceButton } from "./voice/VoiceButton";
import { VoiceModal } from "./voice/VoiceModal";
import { preloadVoiceSdk } from "./voice/voice-session";
import { WelcomeCard } from "./welcome";

/**
 * Polling cadence for the new-messages endpoint. Thirty seconds keeps
 * operator email replies visible quickly without hammering the API
 * for tabs that sit open for hours. We pause when the tab is
 * backgrounded (visibilitychange listener below) so an inactive tab
 * costs nothing.
 */
const POLL_INTERVAL_MS = 30 * 1000;

/**
 * High-level panel surface. We model it as a tiny state machine
 * because the visitor moves between three distinct surfaces:
 *
 *   - "welcome": calm intro card with channel pills and a CTA.
 *   - "chat": the message thread + composer.
 *   - the voice modal sits on top of chat (existing behaviour).
 *
 * The transition out of "welcome" happens once per panel-open (the
 * visitor taps "Start gesprek" or the demo bridge sends a message).
 * Going back from chat returns to welcome but PRESERVES the
 * conversation history so a visitor who briefly checks the contact
 * pills does not lose context.
 */
type PanelView = "welcome" | "chat";

/**
 * Feature-detection guard. Every browser we care about supports
 * FormData + File, but the fallback line keeps us from breaking on
 * exotic webviews that strip File. Caller hides the attach button
 * when this is false.
 */
const SUPPORTS_FILE_UPLOAD =
  typeof FormData !== "undefined" && typeof File !== "undefined" && typeof URL !== "undefined";

/** Allowed mime types per the backend `/widget/v1/uploads` contract. */
const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function uploadErrorMessage(kind: UploadErrorKind, t: ReturnType<typeof tFor>): string {
  switch (kind) {
    case "too_large":
      return t.attach_error_too_large;
    case "unsupported_type":
      return t.attach_error_unsupported_type;
    default:
      return t.attach_error_generic;
  }
}

let nextLocalAttachmentId = 0;
function makeLocalAttachmentId(): string {
  nextLocalAttachmentId += 1;
  return `att_${Date.now().toString(36)}_${nextLocalAttachmentId}`;
}

interface Props {
  boot: WidgetBootConfig;
  /** Demo bridge, only wired when `boot.openByDefault` is true. */
  registerSendFn?: (sender: (text: string) => void) => void;
}

/**
 * Validate a hex color the operator typed in the dashboard. We accept
 * 6 or 8 hex characters with a leading `#` and reject anything else
 * back to coral. CSS variables would silently degrade with an
 * invalid value, which masks operator mistakes.
 */
function clampAccent(raw: string | null | undefined): string {
  if (!raw) return "#FF5C39";
  const v = raw.trim();
  return /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(v) ? v : "#FF5C39";
}

function firstLetter(name: string): string {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed[0]!.toUpperCase() : "Y";
}

export function WidgetApp({ boot, registerSendFn }: Props) {
  const [open, setOpen] = useState(boot.openByDefault);
  // Panel starts on the welcome card unless the demo bridge is wired,
  // in which case we land directly in chat so a "Try me" embed on the
  // marketing page can call __rh_send__("...") without an extra tap.
  const [view, setView] = useState<PanelView>(boot.openByDefault ? "chat" : "welcome");
  const [tenant, setTenant] = useState<WidgetTenantConfig | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorSessionToken, setVisitorSessionToken] = useState<string | null>(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  // Email-capture form lifecycle. The form follows a no_source
  // escalation in the same conversation; it transitions to `done`
  // when the visitor's email lands on the backend, and that state
  // persists for the rest of the panel session so reopening does
  // not re-prompt. CLAUDE.md sectie 3: do not promise things we
  // cannot keep, for anonymous visitors this state IS the promise.
  const [emailCapture, setEmailCapture] = useState<EmailCaptureState>({ kind: "idle" });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Hold the picked-file bytes by local_id so a "retry" click can
  // re-POST the original File without re-prompting the operator.
  const fileBlobsRef = useRef<Map<string, File>>(new Map());
  // Refs so the demo's registered send function reads current state
  // every call instead of closing over first-render values.
  const turnsRef = useRef<ChatTurn[]>(turns);
  const statusRef = useRef<Status>(status);
  const conversationIdRef = useRef<string | null>(conversationId);
  const visitorSessionTokenRef = useRef<string | null>(visitorSessionToken);
  // Track the most-recently rendered message timestamp so the polling
  // cursor advances monotonically across SSE turns and poll fetches.
  // Stored on a ref because the polling effect must read the latest
  // value without resubscribing on every render.
  const lastSeenAtRef = useRef<string | null>(null);
  const lastSeenIdRef = useRef<string | null>(null);
  // Render-id sets so a streamed message and a poll-fetched message
  // with the same persisted id only render once. Streamed turns have
  // no id, so we additionally dedupe by `(role, content, createdAt)`
  // when the poll fetch returns rows the SSE channel already showed.
  const seenMessageIdsRef = useRef<Set<string>>(new Set());
  turnsRef.current = turns;
  statusRef.current = status;
  conversationIdRef.current = conversationId;
  visitorSessionTokenRef.current = visitorSessionToken;

  // Eagerly fetch tenant config on mount so the launcher can render
  // the agent's portrait + accent color from first paint. Waiting for
  // the panel to open means the launcher always shows the fallback
  // glyph on initial page load, which defeats the "there is someone
  // here" cue the avatar is meant to give. The fetch is a single
  // sub-second GET to `/widget/v1/config` and is cached by the CDN.
  useEffect(() => {
    if (tenant !== null) return;
    let cancelled = false;
    setStatus({ kind: "loading_config" });
    fetchConfig(boot)
      .then((cfg) => {
        if (cancelled) return;
        setTenant(cfg);
        setStatus({ kind: "idle" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Config error";
        setStatus({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [tenant, boot]);

  // Apply tenant accent color to the host element so the launcher and
  // primary buttons pick it up via the `--rh-accent` CSS variable.
  useEffect(() => {
    if (!tenant) return;
    const host = scrollRef.current?.getRootNode();
    if (host && host instanceof ShadowRoot) {
      (host.host as HTMLElement).style.setProperty("--rh-accent", clampAccent(tenant.accent_color));
    }
  }, [tenant]);

  // Warm the ElevenLabs SDK cache as soon as the tenant config tells
  // us voice is enabled. The lazy import on first mic click otherwise
  // pays a 1-2s CDN round-trip + ESM resolve penalty at the exact
  // moment the operator wants to talk. Preloading here means the
  // module is already in the browser cache when they click.
  // Idempotent inside `preloadVoiceSdk`; firing again on tenant change
  // is harmless. We do NOT await: a slow or blocked CDN must not
  // surface as a widget error here, the lazy path still runs on
  // click and reports `connection_failed` if needed.
  useEffect(() => {
    if (tenant?.voice_enabled) preloadVoiceSdk();
  }, [tenant?.voice_enabled]);

  // Track all blob: URLs we have created so we can revoke on unmount.
  // We do not revoke per-turn here because thumbnails in already-sent
  // bubbles still need to render; the unmount sweep is enough since
  // the host shadow root and its children all tear down together.
  const blobUrlsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      for (const u of urls) URL.revokeObjectURL(u);
      urls.clear();
      fileBlobsRef.current.clear();
    };
  }, []);

  // Auto-scroll only when the user is already near the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = turns[turns.length - 1];
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom <= 100;
    if (last?.role === "user" || isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: send reads via refs, register-once is the intent.
  useEffect(() => {
    if (!registerSendFn) return;
    registerSendFn((text: string) => {
      void send(text);
    });
  }, [registerSendFn]);

  // Restore the conversation from localStorage on mount and pull any
  // messages that landed while the tab was closed (operator email
  // replies, late assistant turns from another open tab). The demo
  // bridge bypasses this because `openByDefault` always starts a
  // fresh conversation; persisting it would leak demo chats from
  // visitor to visitor on respondhero.com.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only, reads via refs.
  useEffect(() => {
    if (boot.openByDefault) return;
    const stored = loadVisitorSession(boot.tenant);
    if (!stored) return;
    setConversationId(stored.conversationId);
    setVisitorSessionToken(stored.visitorSessionToken);
    lastSeenAtRef.current = stored.lastSeenMessageAt;
    lastSeenIdRef.current = stored.lastSeenMessageId;
    if (stored.lastSeenMessageId) {
      seenMessageIdsRef.current.add(stored.lastSeenMessageId);
    }
    // The visitor cannot see the bubbles we already rendered last
    // session (we did not persist transcript bytes), so the new
    // arrivals appear on a blank thread. That is the right calm
    // restore: we surface what they missed without re-flooding them
    // with messages they already read in a previous session.
    const cursorIso = stored.lastSeenMessageAt ?? new Date(0).toISOString();
    fetchNewMessages(boot, stored.conversationId, stored.visitorSessionToken, new Date(cursorIso))
      .then((rows) => {
        if (rows.length === 0) return;
        // Materialise into ChatTurn entries via the shared helper
        // below so the operator_email styling stays consistent.
        applyPolledMessages(rows);
      })
      .catch((err) => {
        if (err instanceof PollError && err.kind === "forbidden") {
          // Token or row no longer valid (tenant rotated key,
          // legacy row, etc.). Clean slate so the next chat send
          // creates a fresh conversation.
          clearVisitorSession(boot.tenant);
          setConversationId(null);
          setVisitorSessionToken(null);
          lastSeenAtRef.current = null;
          lastSeenIdRef.current = null;
          seenMessageIdsRef.current = new Set();
        }
        // Network or generic errors: stay silent. The 30-second
        // polling loop below will retry on its own cadence the
        // moment the panel is open + visible.
      });
  }, []);

  // Polling loop: fetch new messages every 30s while the panel is
  // open AND the tab is visible. We pause aggressively because the
  // operator value is "visitor with an open tab sees the reply
  // without refreshing"; a backgrounded tab can wait until it comes
  // back to the foreground. Tied to `open` so the launcher state
  // does not poll either (the visitor cannot see anything anyway).
  useEffect(() => {
    if (!open) return;
    if (boot.openByDefault) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function tick(): Promise<void> {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      const convId = conversationIdRef.current;
      const token = visitorSessionTokenRef.current;
      if (!convId || !token) return;
      const cursor = lastSeenAtRef.current ?? new Date(0).toISOString();
      try {
        const rows = await fetchNewMessages(boot, convId, token, new Date(cursor));
        if (cancelled) return;
        if (rows.length > 0) applyPolledMessages(rows);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PollError && err.kind === "forbidden") {
          // Reset to a blank state. Hallon: a 403 means the token
          // does not match the conversation; rather than leak that
          // mismatch we drop everything and let the visitor start
          // fresh on their next message.
          clearVisitorSession(boot.tenant);
          setConversationId(null);
          setVisitorSessionToken(null);
          lastSeenAtRef.current = null;
          lastSeenIdRef.current = null;
          seenMessageIdsRef.current = new Set();
        }
        // Other errors: swallow. The next tick retries.
      }
    }

    function start(): void {
      if (intervalId !== null) return;
      // Fire once immediately on (re-)visibility so the visitor does
      // not wait a full 30s after un-backgrounding the tab.
      void tick();
      intervalId = setInterval(() => {
        void tick();
      }, POLL_INTERVAL_MS);
    }

    function stop(): void {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") start();
      else stop();
    }

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [open, boot]);

  /**
   * Fold one batch of poll-fetched messages into the rendered thread.
   *
   * Dedup rules:
   *   - Skip any message whose persisted id is already in
   *     `seenMessageIdsRef`. Covers the case where the same message
   *     comes in via both the SSE channel (recorded with id at
   *     persist time) and a subsequent poll fetch.
   *   - On insert, mark the id as seen AND advance `lastSeenAtRef`
   *     to the max(created_at) so the next poll's `?after=` cursor
   *     moves forward.
   *   - Persist the updated cursor to localStorage so a page reload
   *     does not re-render the same operator reply.
   */
  function applyPolledMessages(rows: PolledMessage[]): void {
    if (rows.length === 0) return;
    const fresh: ChatTurn[] = [];
    let maxAt = lastSeenAtRef.current;
    let lastId = lastSeenIdRef.current;
    for (const row of rows) {
      if (seenMessageIdsRef.current.has(row.id)) continue;
      seenMessageIdsRef.current.add(row.id);
      fresh.push({
        // Operator emails and assistant turns both render as the
        // assistant-side bubble; user turns from the polling fetch
        // (rare, but possible if a second open tab sent one) reuse
        // the user-side bubble.
        role: row.role === "user" ? "user" : "assistant",
        text: row.content,
        messageId: row.id,
        sourceRole: row.role,
        createdAt: row.created_at,
      });
      if (!maxAt || row.created_at > maxAt) {
        maxAt = row.created_at;
        lastId = row.id;
      }
    }
    if (fresh.length === 0) return;
    setTurns((curr) => [...curr, ...fresh]);
    lastSeenAtRef.current = maxAt;
    lastSeenIdRef.current = lastId;
    const convId = conversationIdRef.current;
    const token = visitorSessionTokenRef.current;
    if (convId && token) {
      saveVisitorSession(boot.tenant, {
        conversationId: convId,
        visitorSessionToken: token,
        lastSeenMessageId: lastId,
        lastSeenMessageAt: maxAt,
      });
    }
  }

  const locale = resolveLocale(boot.locale, tenant?.locale ?? null);
  const t = tFor(locale);

  const opener =
    tenant?.opener_text[locale] ?? tenant?.opener_text[tenant.locale] ?? t.opener_default;

  const chatbotName = tenant?.chatbot_name ?? "Yara";
  const headerTitle = tenant?.brand_name ?? chatbotName;
  const brandLogoUrl = tenant?.widget_brand_logo_url ?? tenant?.logo_url ?? null;
  const avatarUrl = tenant?.chatbot_avatar_url ?? null;
  const avatarLetter = useMemo(() => firstLetter(chatbotName), [chatbotName]);
  // The operator-configurable assistant opening message lands in the
  // first bubble when chat starts. Backend resolves the fallback so
  // this is always a non-empty string once tenant config is loaded.
  const openingMessage = tenant?.opening_message ?? "";

  /**
   * Validate a picked file before we even talk to the server. Mime
   * and size limits mirror the backend contract so the operator gets
   * an instant error string instead of a round-trip to learn about
   * a 413 / 415.
   */
  function preflight(file: File): UploadErrorKind | null {
    if (file.size > MAX_UPLOAD_BYTES) return "too_large";
    if (!ALLOWED_MIME_TYPES.has(file.type)) return "unsupported_type";
    return null;
  }

  /**
   * Kick off the multipart POST in the background and update the
   * matching pending entry on resolve / reject. Optimistic thumbnail
   * stays visible the whole time; the overlay just disappears on
   * success.
   */
  async function runUpload(localId: string, file: File): Promise<void> {
    try {
      const res = await uploadAttachment(boot, file, conversationIdRef.current);
      setPending((curr) =>
        curr.map((a) =>
          a.local_id === localId ? { ...a, state: "ready", attachment_id: res.attachment_id } : a,
        ),
      );
    } catch (err) {
      const kind: UploadErrorKind = err instanceof UploadError ? err.kind : "generic";
      const message = uploadErrorMessage(kind, t);
      setPending((curr) =>
        curr.map((a) => (a.local_id === localId ? { ...a, state: "failed", error: message } : a)),
      );
    }
  }

  function onFilesPicked(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (!SUPPORTS_FILE_UPLOAD) return;
    const additions: PendingAttachment[] = [];
    for (const file of Array.from(files)) {
      const localId = makeLocalAttachmentId();
      const previewUrl = URL.createObjectURL(file);
      blobUrlsRef.current.add(previewUrl);

      const error = preflight(file);
      if (error) {
        additions.push({
          local_id: localId,
          preview_url: previewUrl,
          name: file.name,
          state: "failed",
          error: uploadErrorMessage(error, t),
        });
        continue;
      }

      fileBlobsRef.current.set(localId, file);
      additions.push({
        local_id: localId,
        preview_url: previewUrl,
        name: file.name,
        state: "uploading",
      });
      void runUpload(localId, file);
    }
    setPending((curr) => [...curr, ...additions]);
  }

  function retryUpload(localId: string) {
    const file = fileBlobsRef.current.get(localId);
    if (!file) return;
    setPending((curr) =>
      curr.map((a) =>
        a.local_id === localId ? { ...a, state: "uploading", error: undefined } : a,
      ),
    );
    void runUpload(localId, file);
  }

  function removePending(localId: string) {
    setPending((curr) => {
      const target = curr.find((a) => a.local_id === localId);
      if (target?.preview_url) {
        URL.revokeObjectURL(target.preview_url);
        blobUrlsRef.current.delete(target.preview_url);
      }
      fileBlobsRef.current.delete(localId);
      return curr.filter((a) => a.local_id !== localId);
    });
  }

  async function send(override?: string) {
    const text = (override ?? draft).trim();
    // Snapshot the pending tray so we can ship only ready attachments
    // with this turn. Failed and still-uploading ones stay behind.
    const readyAttachments = pending.filter((a) => a.state === "ready");
    if (statusRef.current.kind === "streaming") return;
    if (!text && readyAttachments.length === 0) return;

    // Mark sent attachments so the in-thread thumbnail keeps rendering
    // but we know not to ship them again with the next turn.
    const sentAttachments: PendingAttachment[] = readyAttachments.map((a) => ({
      ...a,
      state: "sent",
    }));

    const baseTurns = turnsRef.current;
    const userTurn: ChatTurn = {
      role: "user",
      text,
      ...(sentAttachments.length > 0 ? { attachments: sentAttachments } : {}),
    };
    const next: ChatTurn[] = [...baseTurns, userTurn, { role: "assistant", text: "", chunks: [] }];
    setTurns(next);
    // Drop the shipped attachments from the pending tray; failed / still
    // uploading entries stay so the operator can deal with them.
    if (sentAttachments.length > 0) {
      const sentIds = new Set(sentAttachments.map((a) => a.local_id));
      setPending((curr) => curr.filter((a) => !sentIds.has(a.local_id)));
      for (const a of sentAttachments) fileBlobsRef.current.delete(a.local_id);
    }
    if (override === undefined) setDraft("");
    setStatus({ kind: "streaming" });
    const assistantIdx = next.length - 1;

    try {
      const requestMessages = buildRequestMessages(next);

      for await (const ev of streamChat(boot, conversationIdRef.current, requestMessages)) {
        if (ev.kind === "start") {
          setConversationId(ev.conversation_id);
          // First turn of a fresh conversation: the backend emits the
          // visitor_session_token here. Stash it so the polling
          // endpoint can later prove "I am the visitor for this
          // conversation". Subsequent turns omit the token, in which
          // case we keep whatever was already in state (incl. a
          // value restored from localStorage on mount).
          if (ev.visitor_session_token) {
            setVisitorSessionToken(ev.visitor_session_token);
            saveVisitorSession(boot.tenant, {
              conversationId: ev.conversation_id,
              visitorSessionToken: ev.visitor_session_token,
              lastSeenMessageId: lastSeenIdRef.current,
              lastSeenMessageAt: lastSeenAtRef.current,
            });
          }
        } else if (ev.kind === "delta") {
          setTurns((curr) => {
            const copy = curr.slice();
            const slot = copy[assistantIdx];
            if (slot) {
              const prev = slot.chunks ?? [];
              copy[assistantIdx] = {
                ...slot,
                text: slot.text + ev.text,
                chunks: [...prev, ev.text],
              };
            }
            return copy;
          });
        } else if (ev.kind === "tool_call") {
          setTurns((curr) => {
            const copy = curr.slice();
            const slot = copy[assistantIdx];
            if (slot) copy[assistantIdx] = { ...slot, tool: { name: ev.tool_name } };
            return copy;
          });
        } else if (ev.kind === "tool_result") {
          setTurns((curr) => {
            const copy = curr.slice();
            const slot = copy[assistantIdx];
            if (slot) {
              const kbConsulted =
                slot.kbConsulted || (ev.tool_name === "search_knowledge_base" && ev.ok === true);
              copy[assistantIdx] = {
                ...slot,
                tool: { name: ev.tool_name, ok: ev.ok },
                kbConsulted,
              };
            }
            return copy;
          });
        } else if (ev.kind === "done") {
          // Surface the auto-flag onto the assistant turn so the bubble
          // can decide whether to render the no_source email-capture
          // follow-up. The flag only appears on the no_source path;
          // every other close lands without a payload and the field
          // stays undefined.
          if (ev.auto_flagged_reason) {
            const reason = ev.auto_flagged_reason;
            setTurns((curr) => {
              const copy = curr.slice();
              const slot = copy[assistantIdx];
              if (slot) copy[assistantIdx] = { ...slot, autoFlaggedReason: reason };
              return copy;
            });
          }
        } else if (ev.kind === "error") {
          throw new Error(ev.message);
        }
      }
      setStatus({ kind: "idle" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message || t.error_generic : t.error_generic;
      setStatus({ kind: "error", message });
      setTurns((curr) => curr.slice(0, assistantIdx));
    }
  }

  function reset() {
    setTurns([]);
    setConversationId(null);
    setVisitorSessionToken(null);
    setStatus({ kind: "idle" });
    setDraft("");
    // Drop the persistent session entry too: a "Nieuw gesprek" click
    // explicitly asks for a clean slate, including across pageloads.
    clearVisitorSession(boot.tenant);
    lastSeenAtRef.current = null;
    lastSeenIdRef.current = null;
    seenMessageIdsRef.current = new Set();
    // Drop any pending attachments too: the customer asked for a fresh
    // conversation, the half-uploaded photo from the previous one is no
    // longer in scope. Revoke their object URLs so we do not leak.
    setPending((curr) => {
      for (const a of curr) {
        URL.revokeObjectURL(a.preview_url);
        blobUrlsRef.current.delete(a.preview_url);
      }
      return [];
    });
    fileBlobsRef.current.clear();
    // A "new conversation" wipes the email-capture lifecycle too. The
    // backend will refuse a new POST against the same conversation
    // (409 customer_email_already_set), but the fresh thread has a
    // new conversation_id so the form needs to be back at idle.
    setEmailCapture({ kind: "idle" });
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  if (!open) {
    // Avatar-first launcher: when the tenant has configured a portrait
    // we show it inside a 72px circle with a 2px coral ring and a
    // small green online dot. The portrait gives visitors an instant
    // "there is someone here" cue, which a chat glyph alone does not.
    // Fallback to the coral fill + glyph when the operator has not
    // uploaded an avatar yet, so the launcher is never empty.
    const launcherClass = avatarUrl ? "rh-launcher" : "rh-launcher rh-launcher-no-avatar";
    return (
      <button
        type="button"
        class={launcherClass}
        aria-label={t.launcher_aria}
        onClick={() => setOpen(true)}
      >
        {avatarUrl ? (
          <img class="rh-launcher-avatar" src={avatarUrl} alt="" aria-hidden="true" />
        ) : (
          <ChatIcon />
        )}
        <span class="rh-launcher-online-dot" aria-hidden="true" />
      </button>
    );
  }

  // Welcome state. We render this as a separate panel surface so the
  // chat-state composer + thread layout stays clean. Loading tenant
  // config blocks the welcome state; we render a placeholder header
  // + spinner-free body so the visitor never sees a flash of "no
  // brand" before the real config lands.
  if (view === "welcome" && tenant !== null) {
    return (
      <div
        class="rh-panel rh-panel-welcome"
        // biome-ignore lint/a11y/useSemanticElements: matches the existing
        // `<div role="dialog">` pattern used by the chat-state panel below
        // and by VoiceModal; switching only this one site would diverge.
        role="dialog"
        aria-label={headerTitle}
      >
        <div class="rh-panel-handle" aria-hidden="true" />
        <WelcomeCard
          tenant={tenant}
          t={t}
          brandLogoUrl={brandLogoUrl}
          avatarUrl={avatarUrl}
          avatarLetter={avatarLetter}
          onStartConversation={() => setView("chat")}
          onClose={() => {
            setOpen(false);
            setView("welcome");
          }}
        />
      </div>
    );
  }

  // While tenant config is loading on initial welcome render, show a
  // minimal calm shell. We deliberately avoid spinners; the network
  // call is sub-second on healthy paths and the empty header reads
  // as "loading" without animation noise.
  if (view === "welcome" && tenant === null) {
    return (
      <div
        class="rh-panel rh-panel-welcome"
        // biome-ignore lint/a11y/useSemanticElements: see neighbouring branch.
        role="dialog"
        aria-busy="true"
      >
        <div class="rh-panel-handle" aria-hidden="true" />
        <div class="rh-welcome-loading-shell">
          <div class="rh-welcome-loading-strip" aria-hidden="true" />
          <div class="rh-welcome-loading-strip" aria-hidden="true" />
          <div class="rh-welcome-loading-strip" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // First-of-run avatars for assistant rows so consecutive turns share
  // a single mark and long threads stay calm.
  const showAvatarMap = turns.map(
    (turn, i) => turn.role === "assistant" && (i === 0 || turns[i - 1]?.role !== "assistant"),
  );

  const isStreaming = status.kind === "streaming";
  const streamingIdx = isStreaming ? turns.length - 1 : -1;

  // The email-capture form follows the LAST assistant message in the
  // thread, and only when:
  //   - that message carries the no_source auto-flag (the canned
  //     pre-gate escalation), AND
  //   - the form is not currently in `done` state for this panel
  //     session (success persists so reopening does not re-prompt).
  // Streaming-in-progress hides the form: we wait until the message
  // is fully landed before asking the visitor for their email so the
  // follow-up does not pop in under a half-rendered escalation line.
  const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;
  const showEmailCapture =
    !isStreaming &&
    conversationId !== null &&
    lastTurn?.role === "assistant" &&
    lastTurn?.autoFlaggedReason === "no_source" &&
    emailCapture.kind !== "done";

  async function submitEmailCapture(rawEmail: string): Promise<void> {
    const email = rawEmail.trim();
    if (!conversationId) return;
    // Quick client-side gate so the inline error fires instantly on an
    // obviously-broken value. The backend re-validates with
    // Pydantic's `EmailStr`; this is just a courtesy short-circuit.
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCapture({ kind: "error_invalid" });
      return;
    }
    setEmailCapture({ kind: "submitting" });
    const result = await submitCustomerEmail(boot, conversationId, email);
    if (result.kind === "saved" || result.kind === "already_set") {
      setEmailCapture({ kind: "done", email });
      return;
    }
    if (result.kind === "invalid") {
      setEmailCapture({ kind: "error_invalid" });
      return;
    }
    setEmailCapture({ kind: "error_network" });
  }
  const hasDraft = draft.trim().length > 0;
  const hasReadyAttachment = pending.some((a) => a.state === "ready");
  const composerBusy = status.kind === "streaming" || status.kind === "loading_config";
  // Send overrides mic when there is something to send: typed text or a
  // ready attachment. The mic stays the primary affordance otherwise.
  const showSend = hasDraft || hasReadyAttachment;
  const showMic = !hasDraft && !hasReadyAttachment && tenant?.voice_enabled === true;
  const showAttach = SUPPORTS_FILE_UPLOAD;

  return (
    <div class="rh-panel" role="dialog" aria-label={headerTitle}>
      <div class="rh-panel-handle" aria-hidden="true" />
      <div class="rh-header rh-header-chat">
        <div class="rh-header-brand">
          {/*
           * Back arrow on the chat-state header returns to the welcome
           * card without closing the panel. CLAUDE.md sectie 9 visitor
           * flow: the × is the only "close everything" affordance.
           */}
          <button
            type="button"
            class="rh-icon-btn rh-back-btn"
            aria-label={t.welcome_back_aria}
            title={t.welcome_back_aria}
            onClick={() => setView("welcome")}
          >
            <BackArrowIcon />
          </button>
          <div class="rh-chat-agent">
            <div class="rh-chat-agent-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" aria-hidden="true" />
              ) : (
                <span>{avatarLetter}</span>
              )}
            </div>
            <div class="rh-header-text">
              <h1 class="rh-header-name">{chatbotName}</h1>
              <span class="rh-header-agent-tag">{t.chat_header_agent_tag}</span>
            </div>
          </div>
        </div>
        <div class="rh-header-actions">
          {turns.length > 0 ? (
            <button
              type="button"
              class="rh-icon-btn"
              aria-label={t.reset}
              title={t.reset}
              onClick={reset}
              disabled={isStreaming}
            >
              <ResetIcon />
            </button>
          ) : null}
          <button
            type="button"
            class="rh-icon-btn"
            aria-label="Close"
            onClick={() => {
              setOpen(false);
              setView("welcome");
            }}
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <div class="rh-conversation" ref={scrollRef}>
        {turns.length === 0 ? (
          // Empty state: the assistant's first bubble is the
          // operator-configurable opening message (resolved with the
          // locale-aware fallback by the backend). Two suggested-
          // prompt chips sit underneath so the visitor has a one-tap
          // entry point. The opener stays on the bubble itself; the
          // bigger centred-avatar empty state from the pre-welcome
          // redesign is gone because the welcome card now carries
          // that role.
          <div class="rh-empty-chat">
            <div class="rh-row rh-row-assistant">
              <div class="rh-avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" aria-hidden="true" />
                ) : (
                  <span>{avatarLetter}</span>
                )}
              </div>
              <div class="rh-bubble rh-bubble-assistant">
                <span>{openingMessage || opener}</span>
              </div>
            </div>
          </div>
        ) : null}
        {turns.map((turn, i) => (
          <Row
            key={i}
            role={turn.role}
            showAvatar={Boolean(showAvatarMap[i])}
            avatarUrl={avatarUrl}
            letter={avatarLetter}
          >
            <Bubble turn={turn} t={t} streaming={i === streamingIdx} brandName={headerTitle} />
          </Row>
        ))}
        {showEmailCapture ? (
          // Render directly inside the conversation flow (no Row
          // wrapper). The form is wide enough that the assistant
          // avatar gutter would push it past the 88% max-width and
          // crowd the right edge; the gutter is a chat affordance,
          // not appropriate for an input surface.
          <div class="rh-row rh-row-assistant">
            <div class="rh-avatar-spacer" aria-hidden="true" />
            <NoSourceEmailForm
              t={t}
              state={emailCapture}
              onSubmit={(value) => {
                void submitEmailCapture(value);
              }}
            />
          </div>
        ) : null}
        {emailCapture.kind === "done" ? (
          <div class="rh-row rh-row-assistant">
            <div class="rh-avatar-spacer" aria-hidden="true" />
            <div class="rh-no-source-thanks">
              {format(t.no_source_email_thanks, { email: emailCapture.email })}
            </div>
          </div>
        ) : null}
        {status.kind === "error" ? <div class="rh-error">{status.message}</div> : null}
      </div>

      <div class="rh-composer-wrap">
        {pending.length > 0 ? (
          <div class="rh-pending-tray" aria-live="polite">
            {pending.map((att) => (
              <PendingThumb
                key={att.local_id}
                att={att}
                t={t}
                onRetry={() => retryUpload(att.local_id)}
                onRemove={() => removePending(att.local_id)}
              />
            ))}
          </div>
        ) : null}
        <div class="rh-composer">
          {showAttach ? (
            <>
              <button
                type="button"
                class="rh-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={composerBusy}
                aria-label={t.attach_aria}
                title={t.attach_label}
              >
                <AttachIcon />
              </button>
              <input
                ref={fileInputRef}
                class="rh-attach-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                onChange={(e) => {
                  const target = e.target as HTMLInputElement;
                  onFilesPicked(target.files);
                  // Reset so the same file can be picked again after
                  // removal or retry.
                  target.value = "";
                }}
              />
            </>
          ) : null}
          <textarea
            rows={1}
            value={draft}
            placeholder={t.composer_placeholder}
            aria-label={t.composer_input_aria}
            onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
            onKeyDown={onKeyDown}
            disabled={composerBusy}
          />
          {showSend ? (
            <button
              type="button"
              class="rh-send"
              onClick={() => void send()}
              disabled={composerBusy}
              aria-label={t.send}
              title={t.send}
            >
              <SendIcon />
            </button>
          ) : showMic ? (
            <VoiceButton
              ariaLabel={t.voice_button_aria}
              onClick={() => setVoiceOpen(true)}
              disabled={isStreaming}
            />
          ) : null}
        </div>
        <p class="rh-disclaimer">{t.ai_disclaimer}</p>
      </div>
      {voiceOpen ? <VoiceModal boot={boot} t={t} onClose={() => setVoiceOpen(false)} /> : null}
    </div>
  );
}

interface RowProps {
  role: "user" | "assistant";
  showAvatar: boolean;
  avatarUrl: string | null;
  letter: string;
  children: preact.ComponentChildren;
}

/**
 * Layout shell for one chat row. Avatar appears on the first assistant
 * turn of a consecutive run; user rows never show one.
 */
function Row({ role, showAvatar, avatarUrl, letter, children }: RowProps) {
  const cls = role === "user" ? "rh-row rh-row-user" : "rh-row rh-row-assistant";
  return (
    <div class={cls}>
      {role === "assistant" && showAvatar ? (
        <div class="rh-avatar">
          {avatarUrl ? <img src={avatarUrl} alt="" aria-hidden="true" /> : <span>{letter}</span>}
        </div>
      ) : role === "assistant" ? (
        <div class="rh-avatar-spacer" aria-hidden="true" />
      ) : null}
      {children}
    </div>
  );
}

interface BubbleProps {
  turn: ChatTurn;
  t: ReturnType<typeof tFor>;
  streaming: boolean;
  /** Tenant brand_name interpolated into the operator-email label. */
  brandName: string;
}

function Bubble({ turn, t, streaming, brandName }: BubbleProps) {
  const cls = turn.role === "user" ? "rh-bubble rh-bubble-user" : "rh-bubble rh-bubble-assistant";
  const isEmpty = turn.text.length === 0;
  const showTyping = isEmpty && !turn.tool && streaming;
  const isAssistant = turn.role === "assistant";
  const showToolLine =
    turn.tool && (turn.tool.ok === false || (turn.tool.ok === undefined && isEmpty));
  const showSourceChip = isAssistant && turn.kbConsulted === true && !streaming;
  const attachments = turn.attachments ?? [];
  // Operator-email rows (the operator replied via email from the
  // dashboard) get a small "Bericht van het team" label so the
  // visitor reads them as a teammate's note, not as another AI turn.
  // We keep the same bubble background to maintain calm continuity:
  // the customer should not feel jolted by a channel switch.
  const isOperatorEmail = turn.sourceRole === "operator_email";
  if (isOperatorEmail) {
    return (
      <div class="rh-operator-email">
        <div class="rh-operator-email-label">
          {format(t.operator_email_label, { brand: brandName })}
        </div>
        <div class={cls}>
          <span>{turn.text}</span>
        </div>
      </div>
    );
  }
  return (
    <div class={cls}>
      {attachments.length > 0 ? (
        <div class="rh-bubble-thumbs">
          {attachments.map((att) => (
            <div class="rh-thumb" key={att.local_id}>
              <img src={att.preview_url} alt={att.name} />
            </div>
          ))}
        </div>
      ) : null}
      {showToolLine && turn.tool ? (
        <div class="rh-tool-line">
          {turn.tool.ok === false
            ? format(t.tool_failed, { name: turn.tool.name })
            : format(t.tool_running, { name: turn.tool.name })}
        </div>
      ) : null}
      {showTyping ? (
        <span class="rh-typing-dots" aria-label="Typing">
          <span />
          <span />
          <span />
        </span>
      ) : null}
      {turn.text ? (
        <span>
          {isAssistant && turn.chunks && turn.chunks.length > 0
            ? turn.chunks.map((chunk, idx) => (
                <span key={idx} class="rh-token-fade">
                  {chunk}
                </span>
              ))
            : turn.text}
          {streaming && isAssistant ? <span class="rh-cursor" aria-hidden="true" /> : null}
        </span>
      ) : null}
      {showSourceChip ? (
        <span class="rh-source-chip" aria-label={t.source_consulted}>
          <span class="rh-source-chip-dot" aria-hidden="true" />
          {t.source_consulted}
        </span>
      ) : null}
    </div>
  );
}

interface NoSourceEmailFormProps {
  t: ReturnType<typeof tFor>;
  state: EmailCaptureState;
  onSubmit: (email: string) => void;
}

/**
 * Email-capture form that follows the canned no_source escalation.
 *
 * Rendered as an assistant-side follow-up: it sits inside the same
 * row layout as a bubble so the visual rhythm of the thread is
 * preserved. The form is the visible promise that the canned reply
 * "ik laat een collega er even naar kijken" is actually keepable for
 * anonymous visitors (CLAUDE.md sectie 3, Hallon).
 *
 * Microcopy is locale-aware via the strings table; copy choices are
 * deliberately calm and warm (no exclamation marks, sentence case,
 * tutoyer in Dutch / English / German / French).
 */
function NoSourceEmailForm({ t, state, onSubmit }: NoSourceEmailFormProps) {
  const [value, setValue] = useState("");
  const busy = state.kind === "submitting";
  const errorMessage =
    state.kind === "error_invalid"
      ? t.no_source_email_invalid_error
      : state.kind === "error_network"
        ? t.no_source_email_network_error
        : null;
  return (
    <form
      class="rh-no-source-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy) return;
        onSubmit(value);
      }}
    >
      <p class="rh-no-source-title">{t.no_source_email_prompt_title}</p>
      <p class="rh-no-source-body">{t.no_source_email_prompt_body}</p>
      <div class="rh-no-source-row">
        <input
          type="email"
          inputMode="email"
          autocomplete="email"
          class="rh-no-source-input"
          placeholder={t.no_source_email_input_placeholder}
          aria-label={t.no_source_email_input_placeholder}
          value={value}
          disabled={busy}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
        />
        <button
          type="submit"
          class="rh-no-source-send"
          disabled={busy || value.trim().length === 0}
        >
          {t.no_source_email_send_label}
        </button>
      </div>
      {errorMessage ? <p class="rh-no-source-error">{errorMessage}</p> : null}
    </form>
  );
}

interface PendingThumbProps {
  att: PendingAttachment;
  t: ReturnType<typeof tFor>;
  onRetry: () => void;
  onRemove: () => void;
}

/**
 * One slot in the pending-attachment tray above the composer.
 *
 * - `uploading`: thumbnail with a translucent overlay showing the
 *   "Bezig met versturen" microcopy. Operator can already type while
 *   the upload finishes in the background.
 * - `ready`: thumbnail + remove button, no overlay.
 * - `failed`: thumbnail with a coral overlay, an inline "Probeer
 *   opnieuw" link, and the operator-friendly error string.
 */
function PendingThumb({ att, t, onRetry, onRemove }: PendingThumbProps) {
  const failed = att.state === "failed";
  const uploading = att.state === "uploading";
  return (
    <div>
      <div class={failed ? "rh-thumb rh-thumb-failed" : "rh-thumb"}>
        <img src={att.preview_url} alt={att.name} />
        {uploading ? <div class="rh-thumb-overlay">{t.attach_uploading}</div> : null}
        {failed ? <div class="rh-thumb-overlay">{t.attach_remove}</div> : null}
        {!uploading ? (
          <button
            type="button"
            class="rh-thumb-remove"
            aria-label={t.attach_remove}
            title={t.attach_remove}
            onClick={onRemove}
          >
            x
          </button>
        ) : null}
      </div>
      {failed ? (
        <div class="rh-thumb-error">
          {att.error ?? t.attach_error_generic}
          <button type="button" class="rh-thumb-retry" onClick={onRetry}>
            {t.attach_retry}
          </button>
        </div>
      ) : null}
    </div>
  );
}
